import { useCallback } from 'react';

import { useTranslate } from '@i18n';
import { useNavigate } from 'react-router';

import { useUpdateActivityTags } from '@queries';
import { Button, Modal } from '@ui';

import * as css from './TagSelection.css';

type TagSaveModalProps = {
    activityId: number;
    onDismiss: () => void;
    onClose: () => void;
    selectedTags: number[];
    onCreateNewActivity?: () => Promise<number>;
};

export const TagSaveModal = ({
    onDismiss,
    onCreateNewActivity,
    selectedTags,
    activityId,
    onClose,
}: TagSaveModalProps) => {
    const navigate = useNavigate();
    const { mutateAsync: updateActivityTags } = useUpdateActivityTags();

    const updateCurrentActivty = useCallback(async () => {
        await updateActivityTags({
            activityId,
            tagIds: selectedTags,
        });
        onClose();
    }, [activityId, navigate, selectedTags, updateActivityTags]);

    const updateNewActivity = useCallback(async () => {
        const newActivityId = await onCreateNewActivity?.();
        if (!newActivityId) {
            throw Error(`New Activity Creation failed: expected number | string got ${newActivityId}`);
        }
        await updateActivityTags({
            activityId: newActivityId,
            tagIds: selectedTags,
        });
    }, [onCreateNewActivity, navigate, selectedTags, updateActivityTags]);

    // Add to localize once ready
    const question = onCreateNewActivity
        ? 'Create a new activity or update current one?'
        : 'Dp you want to update current activity?';

    const t = useTranslate();
    return (
        <Modal
            title={t('tag_selection.update_prompt')}
            open
            variant="partial"
            footer={{
                primary: (
                    <div className={css.modalButtons}>
                        {onCreateNewActivity ? (
                            <Button variant="primary" onClick={updateNewActivity}>
                                {t('tag_selection.create_new')}
                            </Button>
                        ) : null}
                        <Button variant="primary" onClick={updateCurrentActivty}>
                            {t('tag_selection.update_current')}
                        </Button>
                    </div>
                ),
                secondary: (
                    <Button variant="secondary" onClick={onDismiss}>
                        Cancel
                    </Button>
                ),
            }}
            onDismiss={onDismiss}
        >
            <p>{question}</p>
        </Modal>
    );
};
